import {
  useEffect, useState, useRef, useCallback, FormEvent, ChangeEvent, ReactNode,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getProject, sendMessage, resetRoleSession,
  type EvidenceType as ApiEvidenceType,
  type Project, type Message,
} from '../api/projects';

import {
  BigButton, ScoreBadge, EmptyState, LoadingDots,
  type EvidenceType as PickerEvidenceType,
} from '../components/ChildFriendly';
import { PersonaAvatar } from './PersonaAvatar';
import { TypingDots } from './TypingDots';
import { RoleIntroCard } from './RoleIntroCard';
import { Confetti } from './Confetti';
import { useAuth } from '../auth/AuthContext';

type EvidenceType = PickerEvidenceType;

interface ProjectEvidence {
  id: string;
  icon: string;
  name: string;
  fileUrl?: string | null;
  type: EvidenceType;
  desc: string;
}

export default function RoleSessionPage() {
  const { projectId, roleId } = useParams<{
    projectId: string;
    roleId: string;
  }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTeacher = user?.role === 'teacher';

  const [project, setProject] = useState<Project | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [evidenceType, setEvidenceType] = useState<EvidenceType>('metin');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const [evidences, setEvidences] = useState<ProjectEvidence[]>([]);
  const [introShown, setIntroShown] = useState(false);
  const [showTyping, setShowTyping] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [celebrateId, setCelebrateId] = useState<number | null>(null);
  const prevBonusRef = useRef<number>(0);
  const celebrateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const userHasInteractedRef = useRef(false);
  const celebratedRoleIdsRef = useRef<Set<number>>(new Set());
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const projId = Number(projectId);
  const rId = Number(roleId);

  const role = project?.role_sessions.find((r) => r.id === rId);

  useEffect(() => {
    let cancelled = false;
    getProject(projId)
      .then((p) => {
        if (cancelled) return;
        setProject(p);

        const targetRole = p.role_sessions.find((r) => r.id === rId);
        const history = targetRole?.messages ?? [];
        setMessages(history);

        const iconMap: Record<EvidenceType, string> = {
          anket: '📊', foto: '📸', ses: '🎤', video: '🎥',
          cizim: '✏️', metin: '📝',
        };
        const seeded: ProjectEvidence[] = [];
        const seenNames = new Set<string>();

        // Projedeki TÜM rol oturumlarında yüklenmiş dosyaları tara
        for (const rs of p.role_sessions) {
          for (const m of rs.messages ?? []) {
            if (m.sender !== 'student') continue;
            if (!m.attached_file) continue;
            const evType = inferEvidenceType(
              m.attached_file, m.evidence_type,
            );
            const fileName = extractFileNameFromUrl(m.attached_file);
            if (!fileName || seenNames.has(fileName)) continue;
            seenNames.add(fileName);
            seeded.push({
              id: `seed-${m.id}`,
              icon: iconMap[evType] || '📎',
              name: fileName,
              fileUrl: m.attached_file,
              type: evType,
              desc: 'Öğrencinin yüklediği kanıt belgesi.',
            });
          }
        }
        setEvidences(seeded);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail || 'Proje yüklenemedi.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [projId, rId]);

  useEffect(() => {
    setIntroShown(false);
  }, [rId]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, showTyping]);

  useEffect(() => {
    if (file) {
      const type = file.type;
      let evType: EvidenceType = 'foto';
      if (type.startsWith('image/')) evType = 'foto';
      else if (type.startsWith('audio/')) evType = 'ses';
      else if (type.startsWith('video/')) evType = 'video';
      else if (type === 'application/pdf') evType = 'anket';
      setEvidenceType(evType);
    }
  }, [file]);

  useEffect(() => {
    const currentBonus = role?.bonus ?? 0;
    if (currentBonus > prevBonusRef.current) {
      setShowConfetti(true);
    }
    prevBonusRef.current = currentBonus;
  }, [role?.bonus]);

  useEffect(() => {
    if (isTeacher) return; // Öğretmen modunda kutlama tetikleme
    if (celebratedRoleIdsRef.current.has(rId)) return;
    if (!userHasInteractedRef.current) return;
    if (role?.status !== 'passed') return;
    if (role?.score === null || role?.score === undefined) return;

    celebratedRoleIdsRef.current.add(rId);
    const lastAiMsg = [...messages].reverse().find((m) => m.sender === 'ai');
    if (lastAiMsg) setCelebrateId(lastAiMsg.id);
    celebrateTimerRef.current = setTimeout(() => setCelebrateId(null), 700);
    return () => {
      if (celebrateTimerRef.current) {
        clearTimeout(celebrateTimerRef.current);
        celebrateTimerRef.current = null;
      }
    };
  }, [role?.status, role?.score, messages, rId, isTeacher]);

  const handleConfettiDone = useCallback(
    () => setShowConfetti(false),
    [],
  );

  const handleCiteEvidence = (evName: string, evType: EvidenceType) => {
    if (isTeacher) return;
    const tag = `[Kanıt: ${evName}] `;
    setInput((prev) => (prev.includes(tag) ? prev : `${prev}${tag}`));
    setEvidenceType(evType);
  };

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isTeacher) return;
    if (!input.trim() && !file) return;
    setError('');
    setSending(true);
    setShowTyping(true);
    userHasInteractedRef.current = true;

    const sendingContent = input.trim();
    const sendingFile = file;
    const sendingEvidenceType = evidenceType;
    const tempStudentMsgId = Date.now();

    // Optimistic Update: Öğrencinin mesajını ANINDA ekranın sağına ekle
    const optimisticMsg: Message = {
      id: tempStudentMsgId,
      sender: 'student',
      content: sendingContent,
      attached_file: sendingFile ? URL.createObjectURL(sendingFile) : null,
      evidence_type: sendingEvidenceType as ApiEvidenceType,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, optimisticMsg]);
    setInput('');
    setFile(null);
    setEvidenceType('metin');

    try {
      const res = await sendMessage(projId, rId, {
        content: sendingContent || undefined,
        file: sendingFile || undefined,
        evidence_type: sendingEvidenceType as ApiEvidenceType,
      });
      // Geçici öğrenci mesajını backend'den gelen gerçek mesajla ve AI yanıtıyla değiştir
      setMessages((prev) =>
        prev.map((m) => (m.id === tempStudentMsgId ? res.message : m)).concat(res.ai_response)
      );
      if (res.message.attached_file) {
        const fileUrl = res.message.attached_file;
        const fileName = extractFileNameFromUrl(fileUrl);
        const evType = inferEvidenceType(fileUrl, res.message.evidence_type);
        const iconMap: Record<EvidenceType, string> = {
          anket: '📊', foto: '📸', ses: '🎤', video: '🎥', cizim: '✏️', metin: '📝',
        };
        setEvidences((prev) => {
          if (prev.some((e) => e.name === fileName || e.fileUrl === fileUrl)) return prev;
          return [
            ...prev,
            {
              id: `seed-${res.message.id}`,
              icon: iconMap[evType] || '📎',
              name: fileName,
              fileUrl: fileUrl,
              type: evType,
              desc: 'Öğrencinin yüklediği kanıt belgesi.',
            },
          ];
        });
      }
      if (res.role_session) {
        setProject((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            role_sessions: prev.role_sessions.map((r) =>
              r.id === rId
                ? {
                    ...r,
                    status: res.role_session!.status,
                    attempts_used: res.role_session!.attempts_used,
                    score: res.role_session!.score,
                    bonus: res.role_session!.bonus,
                  }
                : r
            ),
          };
        });
      }
    } catch (err) {
      // Hata durumunda iyimser eklenen mesajı kaldır
      setMessages((prev) => prev.filter((m) => m.id !== tempStudentMsgId));
      const detail = (
        err as { response?: { data?: { detail?: string } } }
      )?.response?.data?.detail;
      setError(detail || 'Mesaj gönderilemedi.');
    } finally {
      setShowTyping(false);
      setSending(false);
    }
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  }

  // Sidebar "Yeni Kanıt Ekle" — dosyayı mesaj yazma alanına ekler.
  function handleSidebarEvidenceUpload(f: File) {
    if (isTeacher) return;
    setFile(f);
  }

  async function handleResetRole() {
    if (isTeacher) return;
    if (!projId || !rId) return;
    try {
      setLoading(true);
      const updatedProject = await resetRoleSession(projId, rId);
      setProject(updatedProject);
      const currentRole = updatedProject.role_sessions.find((r) => r.id === rId);
      setMessages(currentRole?.messages || []);
      setInput('');
      setFile(null);
      setError('');
    } catch (err) {
      setError('Rol sıfırlanamadı.');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <AppLayoutShim title="Yükleniyor">
        <LoadingDots message="Proje yükleniyor" />
      </AppLayoutShim>
    );
  }
  if (!project) {
    return (
      <AppLayoutShim title="Proje bulunamadı">
        <EmptyState icon="🤔" title="Proje bulunamadı" />
      </AppLayoutShim>
    );
  }
  if (!role) {
    return (
      <AppLayoutShim title="Rol bulunamadı">
        <EmptyState icon="🤔" title="Rol bulunamadı" />
      </AppLayoutShim>
    );
  }

  const isFinished = role.status === 'passed' || role.status === 'failed';

  return (
    <div className="app-layout">
      <AppHeader />
      <main className="container container--wide chat-page-main">
        {isTeacher && (
          <div className="teacher-inspection-banner" style={{ background: '#3b82f6', color: '#ffffff', padding: '0.85rem 1.25rem', borderRadius: 12, marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.2)' }}>
            <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
              👀 <strong>Öğretmen İnceleme Modu:</strong> {role.role_name} Görüşmesi
            </span>
            <button
              type="button"
              className="btn btn--sm teacher-banner-btn"
              onClick={() => navigate('/teacher')}
            >
              ← Öğretmen Paneline Dön
            </button>
          </div>
        )}

        {!introShown && !isTeacher && (
          <RoleIntroCard
            roleName={role.role_name}
            persona={role.persona_type}
            focus={role.role_focus}
            onStart={() => setIntroShown(true)}
          />
        )}
        <Confetti active={showConfetti} onDone={handleConfettiDone} />

        <div className="chat-layout">
          {/* SOL KOLON: Chat & Mesajlaşma */}
          <div className="chat-page">
            <header>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => navigate(`/projects/${projId}`)}
                aria-label="Projeye dön"
              >
                ← Projeye dön
              </button>
              <div className="role-header">
                <PersonaAvatar
                  persona={role.persona_type}
                  roleName={role.role_name}
                  size={40}
                  showBadge
                />
                <h2>{role.role_name}</h2>
                <span className={`status-badge status-${role.status}`}>
                  {statusLabel(role.status)}
                </span>
              </div>
              <div className="attempts" aria-live="polite">
                Deneme: {role.attempts_used} / 3
                {role.score !== null && (
                  <>
                    {' · '}
                    <ScoreBadge
                      band={(role.band || '') as 'yetersiz' | 'yuzeysel' | 'iyi' | 'guclu' | ''}
                      score={role.score}
                    />
                    {role.bonus > 0 && (
                      <span className="chat-bonus">
                        +{role.bonus} bonus 🏆
                      </span>
                    )}
                  </>
                )}
              </div>
            </header>

            <div className="role-focus-bar">
              <span className="info-icon" aria-hidden="true">💡</span>{' '}
              Bakış Açısı: <strong>{role.role_focus}</strong>
            </div>

            <div className="messages" ref={messagesContainerRef} role="log" aria-live="polite">
              {messages.length === 0 && (
                <EmptyState
                  icon="💬"
                  title={`${role.role_name} ile sohbet`}
                  message="Henüz bu mülakat adımı için mesaj kaydı bulunmuyor."
                />
              )}

              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`message message-${m.sender}${celebrateId === m.id ? ' celebrate' : ''}`}
                  data-persona={m.sender === 'ai' ? (role.persona_type ?? 'unknown') : undefined}
                >
                  <div className="sender-label">
                    {m.sender === 'student' ? (isTeacher ? '🧒 Öğrenci' : '🧒 Sen') : `🤖 ${role.role_name}`}
                  </div>
                  <div className="bubble">{m.content}</div>
                  {m.sender === 'student' && m.evidence_type && m.evidence_type !== 'metin' && (
                    <div className="evidence-tag" aria-label={`Kanıt türü: ${m.evidence_type}`}>
                      {evidenceIcon(m.evidence_type)} {evidenceLabel(m.evidence_type)}
                    </div>
                  )}
                  {m.attached_file && (
                    <div className="attachment">
                      📎 <a href={m.attached_file} target="_blank" rel="noreferrer">
                        Dosyayı Aç ↗
                      </a>
                    </div>
                  )}
                </div>
              ))}

              {showTyping && (
                <TypingDots roleName={role.role_name} persona={role.persona_type} />
              )}

              {isFinished && !isTeacher && (
                <div
                  className={`final-banner status-${role.status}`}
                  role="status"
                >
                  {role.status === 'passed' ? (
                    <div className="passed-banner-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '1rem', flexWrap: 'wrap' }}>
                      <div>
                        🎉 <strong>Tebrikler, bu rolü geçtin!</strong>{' '}
                        <ScoreBadge
                          band={(role.band || '') as 'yetersiz' | 'yuzeysel' | 'iyi' | 'guclu' | ''}
                          score={role.score}
                        />
                        {role.bonus > 0 && (
                          <span className="chat-bonus">
                            +{role.bonus} bonus 🏆
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn btn--primary btn--md"
                        onClick={() => navigate(`/projects/${projId}`)}
                      >
                        ➡️ Sonraki Role Geç / Projeye Dön
                      </button>
                    </div>
                  ) : (
                    <div className="failed-banner-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '1rem', flexWrap: 'wrap' }}>
                      <span>💪 Bu rol 3 denemede geçilemedi. İstersen rolü sıfırlayıp yeni bir stratejiyle tekrar deneyebilirsin!</span>
                      <button
                        type="button"
                        className="btn btn--secondary btn--sm"
                        onClick={handleResetRole}
                      >
                        🔄 Rolü Sıfırla ve Tekrar Dene
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {isTeacher ? (
              <div style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 12, padding: '1rem 1.25rem', marginTop: '1rem', textAlign: 'center', color: '#475569', fontWeight: 600, fontSize: '0.9rem' }}>
                👀 Öğretmen İnceleme Modu — Öğrencinin bu kişileştirilmiş YZ ile gerçekleştirdiği diyalog geçmişini inceliyorsunuz.
              </div>
            ) : !isFinished ? (
              <form onSubmit={handleSubmit} className="composer">
                {error && (
                  <div role="alert" className="error">
                    <span aria-hidden="true">😕</span> {error}
                  </div>
                )}

                {file && (
                  <div className="file-preview">
                    <span>📎 <strong>{file.name}</strong> <small style={{ color: 'var(--text-muted)', marginLeft: '8px' }}>(Gönder butonuna bastığında veritabanına yüklenecektir)</small></span>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      aria-label="Dosyayı kaldır"
                    >
                      ✕
                    </button>
                  </div>
                )}

                <div className="composer-row">
                  <label htmlFor="file" className="file-button" aria-label="Dosya ekle">
                    <span aria-hidden="true">📎</span>
                    <input
                      id="file"
                      type="file"
                      accept="image/*,.pdf,.mp3,.mp4"
                      onChange={handleFileChange}
                      disabled={sending}
                      className="visually-hidden"
                    />
                  </label>

                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Mesajını yaz… (Enter ile gönder, Shift+Enter yeni satır)"
                    readOnly={sending}
                    rows={2}
                    aria-label="Mesajınız"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e as unknown as FormEvent);
                      }
                    }}
                  />

                  <BigButton
                    type="submit"
                    variant="primary"
                    icon="🚀"
                    loading={sending}
                    disabled={!input.trim() && !file}
                  >
                    {sending ? 'Gönderiliyor' : 'Gönder'}
                  </BigButton>
                </div>
              </form>
            ) : null}
          </div>

          {/* SAĞ KOLON: Kanıtlarımız & Proje Belgeleri */}
          <aside className="chat-sidebar">
            <div className="sidebar-card">
              <div className="sidebar-header">
                <h3>📁 Proje Kanıtları & Belgeler</h3>
                <p>{isTeacher ? 'Öğrencinin mülakatta sunduğu kanıt ve belgeler.' : 'Yapay zekâyı ikna etmek için hazırladığın kanıtlara tek tıkla atıfta bulun!'}</p>
              </div>

              {evidences.length === 0 ? (
                <div className="empty-sidebar-state">
                  <span className="empty-icon" aria-hidden="true">📂</span>
                  <p><strong>Yüklenmiş kanıt bulunmuyor.</strong></p>
                </div>
              ) : (
                <div className="evidence-list">
                  {evidences.map((ev) => (
                    <div key={ev.id} className="evidence-item-card">
                      <div className="evidence-item-header">
                        <span className="evidence-item-icon" aria-hidden="true">{ev.icon}</span>
                        <div className="evidence-item-info">
                          <strong>{ev.name}</strong>
                          <small>{ev.desc}</small>
                          {ev.fileUrl && (
                            <div style={{ marginTop: '4px' }}>
                              <a
                                href={ev.fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: '0.8rem', color: 'var(--primary)', fontWeight: 600 }}
                              >
                                🔍 Dosyayı Görüntüle ↗
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                      {!isTeacher && (
                        <button
                          type="button"
                          className="btn-cite-evidence"
                          onClick={() => handleCiteEvidence(ev.name, ev.type)}
                          disabled={sending || isFinished}
                        >
                          📌 Kanıta Atıfta Bul
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {!isTeacher && (
                <div className="sidebar-upload-box">
                  <label htmlFor="sidebar-file" className="btn-sidebar-upload">
                    ➕ Yeni Kanıt Ekle
                    <input
                      id="sidebar-file"
                      type="file"
                      accept="image/*,.pdf,.mp3,.mp4"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleSidebarEvidenceUpload(f);
                      }}
                      disabled={sending || isFinished}
                      className="visually-hidden"
                    />
                  </label>
                </div>
              )}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

function extractFileNameFromUrl(url: string): string {
  try {
    const cleaned = url.split('?')[0] ?? url;
    const lastSlash = cleaned.lastIndexOf('/');
    const tail = lastSlash >= 0 ? cleaned.slice(lastSlash + 1) : cleaned;
    const decoded = decodeURIComponent(tail);
    return decoded || '';
  } catch {
    return '';
  }
}

function inferEvidenceType(
  url: string,
  dbType: string | undefined,
): EvidenceType {
  // DB'de kayıtlı tip spesifikse (anket/foto/ses/video/cizim) ona
  // güven. 'metin' ya da tanımsızsa dosya uzantısından çıkar —
  // bu yöntem migration öncesi yüklenen eski kanıtları da yakalar.
  if (dbType && dbType !== 'metin' && dbType !== 'yok') {
    return dbType as EvidenceType;
  }
  const lower = url.toLowerCase().split('?')[0] ?? url.toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(lower)) return 'foto';
  if (/\.(mp3|wav|m4a|ogg|webm)$/i.test(lower)) return 'ses';
  if (/\.(mp4|mov|avi|mkv)$/i.test(lower)) return 'video';
  if (/\.pdf$/i.test(lower)) return 'anket';
  return 'metin';
}

function statusLabel(status: string): string {
  switch (status) {
    case 'pending': return '⏳ Bekliyor';
    case 'active': return '💬 Aktif';
    case 'passed': return '✅ Geçti';
    case 'failed': return '❌ Kaldı';
    default: return status;
  }
}

function evidenceIcon(t: string): string {
  return {
    anket: '📊',
    foto: '📸',
    ses: '🎤',
    video: '🎥',
    cizim: '✏️',
    metin: '📝',
    yok: '❓',
  }[t] ?? '📎';
}

function evidenceLabel(t: string): string {
  return {
    anket: 'Anket / Veri',
    foto: 'Fotoğraf',
    ses: 'Ses Kaydı',
    video: 'Video',
    cizim: 'Çizim / Şema',
    metin: 'Metin',
    yok: 'Kanıt Yok',
  }[t] ?? t;
}

function AppHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const displayName = user?.display_name || user?.username || 'Kullanıcı';
  async function handleLogout() {
    await logout();
    navigate('/login');
  }
  const firstWord = displayName.split(/\s+/)[0] ?? '?';
  const initials = (firstWord.slice(0, 2) || '?').toUpperCase();
  return (
    <header className="app-layout__header" role="banner">
      <div className="container container--wide app-layout__bar">
        <div className="app-layout__brand">
          <span className="app-layout__logo" aria-hidden="true">🎓</span>
          <span className="app-layout__brand-text">Mulakid</span>
        </div>
        <div className="app-layout__user">
          <span className="user-chip">
            <span>{displayName}</span>
            <span className="user-chip__avatar" aria-hidden="true">{initials}</span>
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={handleLogout}
          >
            Çıkış
          </button>
        </div>
      </div>
    </header>
  );
}

function AppLayoutShim({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="app-layout">
      <AppHeader />
      <main className="container">
        <div className="page-header">
          <h1 className="page-title">{title}</h1>
        </div>
        {children}
      </main>
    </div>
  );
}
